import { useState, useEffect } from "react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useTranslation } from "@/hooks/useTranslation";
import type { Guest, CreateGuestPayload } from "@/services/api/eventSettings";

interface GuestModalProps {
  open: boolean;
  mode: "create" | "edit";
  guest?: Guest;
  onOpenChange: (open: boolean) => void;
  onSubmit: (payload: CreateGuestPayload | { id: string; payload: Partial<CreateGuestPayload> }) => Promise<void>;
  loading?: boolean;
}

export function GuestModal({ open, onOpenChange, mode, guest, onSubmit, loading = false }: GuestModalProps) {
  const { t } = useTranslation();
  const [name, setName] = useState("");
  const [type, setType] = useState<"adult" | "kid">("adult");
  const [menuPreference, setMenuPreference] = useState<"standard" | "vegetarian" | "vegan" | undefined>(undefined);
  const [accommodationNeeded, setAccommodationNeeded] = useState(false);
  const [ceremonyAttendance, setCeremonyAttendance] = useState({
    civil: false,
    religious: false,
    party: false,
  });

  useEffect(() => {
    if (guest && mode === "edit") {
      setName(guest.name);
      setType(guest.type);
      setMenuPreference(guest.menuPreference);
      setAccommodationNeeded(guest.accommodationNeeded || false);
      setCeremonyAttendance({
        civil: guest.ceremonyAttendance?.civil ?? false,
        religious: guest.ceremonyAttendance?.religious ?? false,
        party: guest.ceremonyAttendance?.party ?? false,
      });
    } else {
      setName("");
      setType("adult");
      setMenuPreference(undefined);
      setAccommodationNeeded(false);
      setCeremonyAttendance({
        civil: false,
        religious: false,
        party: false,
      });
    }
  }, [guest, mode, open]);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    
    if (!name.trim()) {
      toast.error(t("validation.nameRequired") || t("common.required"));
      return;
    }

    const payload: CreateGuestPayload = {
      name: name.trim(),
      type,
      menuPreference,
      accommodationNeeded: accommodationNeeded || undefined,
      ceremonyAttendance: Object.values(ceremonyAttendance).some((v) => v) ? ceremonyAttendance : undefined,
    };

    try {
      if (mode === "edit" && guest) {
        await onSubmit({ id: guest.id, payload });
      } else {
        await onSubmit(payload);
      }
      onOpenChange(false);
    } catch (error) {
      console.error(error);
      // Error is already handled in the parent component (GuestsTab)
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>
            {mode === "create"
              ? t("guests.addGuest") || t("guests.add")
              : t("guests.editGuest") || t("guests.edit")}
          </DialogTitle>
          <DialogDescription>
            {mode === "create"
              ? t("guests.addGuestDescription") || t("guests.addDescription")
              : t("guests.editGuestDescription") || t("guests.editDescription")}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit}>
          <div className="space-y-4 py-4">
          <div className="grid gap-4 md:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="name">
                {t("guests.name")} <span className="text-destructive">*</span>
              </Label>
              <Input
                id="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder={t("guests.guestNamePlaceholder") || t("guests.namePlaceholder")}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="type">{t("guests.type")}</Label>
              <Select value={type} onValueChange={(v) => setType(v as typeof type)}>
                <SelectTrigger>
                  <SelectValue>
                    {type === "adult" ? t("guests.adult") : t("guests.kid")}
                  </SelectValue>
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="adult">{t("guests.adult")}</SelectItem>
                  <SelectItem value="kid">{t("guests.kid")}</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="menu">{t("guests.menu") || t("rsvp.menuPreference")}</Label>
            <Select
              value={menuPreference || ""}
              onValueChange={(v) => setMenuPreference(v ? (v as typeof menuPreference) : undefined)}
            >
              <SelectTrigger>
                <SelectValue placeholder={t("guests.noPreference") || t("rsvp.selectMenuPreference")}>
                  {menuPreference && (
                    menuPreference === "standard"
                      ? (t("guests.standardFull") || t("rsvp.menu.standard"))
                      : menuPreference === "vegetarian"
                        ? (t("guests.vegetarianFull") || t("rsvp.menu.vegetarian"))
                        : (t("guests.veganFull") || t("rsvp.menu.vegan"))
                  )}
                </SelectValue>
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="">{t("guests.noPreference") || t("rsvp.selectMenuPreference")}</SelectItem>
                <SelectItem value="standard">{t("guests.standardFull") || t("rsvp.menu.standard")}</SelectItem>
                <SelectItem value="vegetarian">{t("guests.vegetarianFull") || t("rsvp.menu.vegetarian")}</SelectItem>
                <SelectItem value="vegan">{t("guests.veganFull") || t("rsvp.menu.vegan")}</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="flex items-center space-x-2">
            <input
              type="checkbox"
              id="accommodation"
              checked={accommodationNeeded}
              onChange={(e) => setAccommodationNeeded(e.target.checked)}
              className="h-4 w-4 rounded border-gray-300"
            />
            <Label htmlFor="accommodation" className="text-sm font-normal cursor-pointer">
              {t("guests.accommodation") || t("rsvp.accommodationNeeded")}
            </Label>
          </div>

          <div className="space-y-2">
            <Label>{t("guests.ceremonyAttendance") || t("rsvp.ceremonyAttendance")}</Label>
            <div className="space-y-2">
              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="civil"
                  checked={ceremonyAttendance.civil}
                  onChange={(e) => setCeremonyAttendance({ ...ceremonyAttendance, civil: e.target.checked })}
                  className="h-4 w-4 rounded border-gray-300"
                />
                <Label htmlFor="civil" className="text-sm font-normal cursor-pointer">
                  {t("events.subEvents.civilCeremony")}
                </Label>
              </div>
              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="religious"
                  checked={ceremonyAttendance.religious}
                  onChange={(e) => setCeremonyAttendance({ ...ceremonyAttendance, religious: e.target.checked })}
                  className="h-4 w-4 rounded border-gray-300"
                />
                <Label htmlFor="religious" className="text-sm font-normal cursor-pointer">
                  {t("events.subEvents.religiousCeremony")}
                </Label>
              </div>
              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="party"
                  checked={ceremonyAttendance.party}
                  onChange={(e) => setCeremonyAttendance({ ...ceremonyAttendance, party: e.target.checked })}
                  className="h-4 w-4 rounded border-gray-300"
                />
                <Label htmlFor="party" className="text-sm font-normal cursor-pointer">
                  {t("events.subEvents.reception")}
                </Label>
              </div>
            </div>
          </div>
        </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={loading}>
              {t("common.cancel")}
            </Button>
            <Button type="submit" disabled={loading}>
              {loading ? t("common.loading") : mode === "create" ? t("common.create") : t("common.save")}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

